import { useEffect, useRef } from "react";

import type { Requirement, Turn } from "@sdcp/api-client";
import { VerityMark } from "./Header";
import { DocumentCard } from "./DocumentCard";
import { ProgressCard } from "./ProgressCard";
import { TypingIndicator } from "./TypingIndicator";

export interface ConversationProps {
  /** The whole checklist — the loan-file card renders it. */
  requirements: Requirement[];
  /** Withdraw a document. Only the borrower's own uploads can be replaced. */
  onReplace?: ((documentId: string) => void) | undefined;
  /** Pick a set-aside document back up, from its row on the card (FR-59). */
  onResume?: ((requirementId: string) => void) | undefined;
  busy?: boolean;
  /**
   * Verity is composing a reply. Shown the instant a message or a tap leaves,
   * because a turn is fifteen seconds of model round-trips and silence reads
   * as broken.
   */
  replying?: boolean;
  /**
   * What the borrower just said, before the server has recorded it. The
   * bubble appears at once; the server's copy replaces it when the turn lands.
   */
  pendingText?: string | null | undefined;
  turns: Turn[];
  verified: number;
  total: number;
  loanReference: string;
}

export function Conversation({
  turns,
  verified,
  total,
  loanReference,
  requirements,
  onReplace,
  onResume,
  busy = false,
  replying = false,
  pendingText,
}: ConversationProps) {
  const endRef = useRef<HTMLDivElement>(null);

  // New turns arrive at the bottom; the borrower should not have to hunt for
  // them — and neither should the sign that one is coming.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length, replying]);

  const last = turns[turns.length - 1];

  return (
    <div className="flex-1 overflow-y-auto bg-bg-app px-4 pt-6 pb-4">
      <VerityIntro />

      <ol className="flex flex-col gap-2">
        {turns.map((turn, i) => (
          <li key={turn.id}>
            {turn.role === "verity" ? (
              <VerityTurn
                requirements={requirements}
                turn={turn}
                showLabel={turns[i - 1]?.role !== "verity"}
                showAvatar={turns[i + 1]?.role !== "verity"}
                verified={verified}
                total={total}
                loanReference={loanReference}
                onResume={onResume}
              />
            ) : (
              <BorrowerTurn turn={turn} onReplace={onReplace} busy={busy} />
            )}
          </li>
        ))}

        {pendingText && replying && (
          <li>
            <div className="flex flex-col items-end gap-2">
              <p className="max-w-[85%] rounded-xl bg-verity-600 px-4 py-3 text-[15px] font-bold text-white opacity-90">
                {pendingText}
              </p>
            </div>
          </li>
        )}

        {replying && (
          <li>
            <TypingIndicator showLabel={Boolean(pendingText) || last?.role !== "verity"} />
          </li>
        )}
      </ol>

      <div ref={endRef} />
    </div>
  );
}

function VerityIntro() {
  return (
    <div className="mb-6 flex flex-col items-center gap-3">
      <div
        className="grid size-[72px] place-items-center rounded-full"
        style={{ background: "var(--verity-orb)", boxShadow: "var(--e-agent)" }}
      >
        <VerityMark className="size-8 [&_path]:fill-white [&_circle]:fill-verity-600" />
      </div>
      <h1 className="text-2xl font-extrabold tracking-tight">Verity</h1>
    </div>
  );
}

function VerityTurn({
  turn,
  showLabel,
  showAvatar,
  verified,
  total,
  loanReference,
  requirements,
  onResume,
}: {
  turn: Turn;
  showLabel: boolean;
  showAvatar: boolean;
  verified: number;
  total: number;
  loanReference: string;
  requirements: Requirement[];
  onResume?: ((requirementId: string) => void) | undefined;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* Labelled once per run of turns, so it reads as one speaker rather than
          a stream of separate notifications. */}
      {showLabel && (
        <p className="font-mono text-[11px] tracking-widest text-verity-600 uppercase">Verity</p>
      )}

      <div className="flex items-end gap-2">
        <span className="w-8 shrink-0">
          {showAvatar && (
            <span
              className="grid size-8 place-items-center rounded-md"
              style={{ background: "var(--brand-grad)" }}
              aria-hidden
            >
              <VerityMark className="size-4 [&_path]:fill-white [&_circle]:fill-indigo-450" />
            </span>
          )}
        </span>

        <div className="max-w-[85%] rounded-xl bg-bubble-agent px-4 py-3">
          <p className="text-[15px] leading-relaxed">{turn.text}</p>
          {turn.document && <DocumentCard document={turn.document} />}
          {turn.show_progress && (
            <ProgressCard
              requirements={requirements}
              verified={verified}
              total={total}
              loanRef={loanReference}
              onResume={onResume}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function BorrowerTurn({
  turn,
  onReplace,
  busy,
}: {
  turn: Turn;
  onReplace?: ((documentId: string) => void) | undefined;
  busy?: boolean;
}) {
  return (
    <div className="flex flex-col items-end gap-2">
      <p className="max-w-[85%] rounded-xl bg-verity-600 px-4 py-3 text-[15px] font-bold text-white">
        {turn.text}
      </p>
      {turn.document && (
        <div className="w-[85%]">
          {/* Only the borrower's own uploads can be withdrawn. A statement a
              bank sent is not theirs to take back, and offering the action
              would promise something the system cannot do. */}
          <DocumentCard document={turn.document} onReplace={onReplace} busy={busy} />
        </div>
      )}
    </div>
  );
}
